import debounce from 'debounce-promise';
import { when } from 'mobx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EmailVerification, TEmailVerificationStatus } from '@feathr/blackbox';
import { EPinpointRequestStatus } from '@feathr/blackbox';
import { isAddressEmpty, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { validate } from '@feathr/rachis';

/**
 * Return value of {@link useSenderVerification}.
 */
export interface IUseSenderVerificationResult {
  /** The current email verification, if one was found or created. */
  verification?: EmailVerification;
  /** The verification status, defaulting to 'Unverified' when there is no verification. */
  status: TEmailVerificationStatus;
  /** True while the verification lookup is in flight. */
  isPending: boolean;
  /** Re-runs the lookup, which initiates verification for an unverified address. */
  handleVerify: () => Promise<void>;
  /** Resends the verification email for the existing verification. */
  handleResend: () => Promise<void>;
  /** True when a verification exists with both a from_name and an address. */
  hasVerificationDefaults: boolean;
}

/**
 * Owns the sender email-verification flow: the debounced lookup, the create/poll,
 * the resulting status, and the verify/resend handlers. A missing verification for a
 * valid email is created and its verification initiated automatically during the lookup.
 *
 * The caller owns campaign/step patching via `onVerificationChange` and any focus tracking.
 *
 * @param email - The sender email address to look up.
 * @param onVerificationChange - Called with the resolved verification (or undefined) whenever the
 *   lookup completes; use it to patch the owning campaign/step/mailing.
 * @returns The verification, its status, pending state, and handlers.
 *
 * @example
 * const { status, handleVerify } = useSenderVerification(
 *   campaign.get('from_address'),
 *   (verification) => campaign.set({ ... }),
 * );
 */
export function useSenderVerification(
  email: string | undefined,
  onVerificationChange?: (verification?: EmailVerification) => void,
): IUseSenderVerificationResult {
  const { t } = useTranslation();
  const { EmailVerifications } = useStore();

  const [verification, setVerification] = useState<EmailVerification | undefined>();
  const [isPending, setIsPending] = useState(true);

  const getEmailStatus = useCallback(
    debounce(async (fromAddress: string | undefined) => {
      validate.single(fromAddress, {
        presence: { allowEmpty: false, message: 'Email address cannot be blank.' },
      });

      setIsPending(true);
      const emailVerifications = EmailVerifications.list(
        { filters: { email: fromAddress } },
        { reset: true },
      );
      await when(() => !emailVerifications.isPending);
      setIsPending(false);

      if (!emailVerifications.models.length) {
        const error: string[] | undefined = validate.single(fromAddress, {
          presence: { allowEmpty: false, message: 'Email address has to be valid.' },
          email: true,
        });

        if (!error) {
          const model = EmailVerifications.create({ email: fromAddress });
          await when(() => !model.isPending);
          const newEmailVerification = await EmailVerifications.add(model);
          await when(() => !newEmailVerification.isPending);

          setVerification(newEmailVerification);
          return newEmailVerification;
        }
      }

      const newEmailVerification =
        !emailVerifications.isErrored && emailVerifications.models.length === 1
          ? emailVerifications.models[0]
          : undefined;
      setVerification(newEmailVerification);

      return newEmailVerification;
    }, 500),
    [EmailVerifications],
  );

  useEffect(() => {
    getEmailStatus(email)
      .then((newEmailVerification) => {
        onVerificationChange?.(newEmailVerification);
      })
      .catch((error) => {
        throw error;
      });
  }, [getEmailStatus, email, onVerificationChange]);

  async function handleVerify(): Promise<void> {
    const updatedEmailVerification = await getEmailStatus(email);
    const updatedEmailStatus = updatedEmailVerification?.get('status') ?? 'Unverified';
    if (updatedEmailStatus === 'Success') {
      toast(t('This email address has been verified.'), { type: 'success' });
      return;
    }

    if (updatedEmailStatus === 'Pending') {
      toast(t('Check your email for a verification link.'), { type: 'info' });
      return;
    }

    const model = EmailVerifications.create({ email });
    const response = await EmailVerifications.add(model);
    if (response.isErrored) {
      toast(t('There was a problem trying to send a verification email.'), {
        type: 'error',
      });
    } else {
      setVerification(response);
      const isVerified = response.get('status') === EPinpointRequestStatus.Success;
      toast(
        isVerified
          ? t('Your email address {{fromAddress}} is verified', {
              fromAddress: email,
            })
          : t(
              'A verification link has been sent to {{fromAddress}}. This link will expire in 24 hours.',
              { fromAddress: email },
            ),
        { type: isVerified ? 'success' : 'info' },
      );
    }
  }

  async function handleResend(): Promise<void> {
    if (verification) {
      const response = await verification.resend();
      if (verification.isErrored) {
        toast(t('There was a problem trying to resend a verification email.'), {
          type: 'error',
        });
        // eslint-disable-next-line no-console
        console.error(response);
      } else {
        toast(
          t(
            'A verification link has been resent to {{fromAddress}}. This link will expire in 24 hours.',
            { fromAddress: email },
          ),
          { type: 'info' },
        );
      }
    }
  }

  const status = verification?.get('status') ?? 'Unverified';
  const hasVerificationDefaults =
    !!verification?.get('from_name') && !isAddressEmpty(verification?.get('address'));

  return {
    verification,
    status,
    isPending,
    handleVerify,
    handleResend,
    hasVerificationDefaults,
  };
}
